import { useState, useEffect, useRef, useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getToken } from '@shared/services/api';
import { invalidateShared, SHARED_KEYS } from '@shared/services/sharedResource';
import { useImpersonationGuard } from '@shared/components/ImpersonationGuard';
import ImpersonationSensitiveBanner from '@shared/components/ImpersonationSensitiveBanner';
import ReadOnlyBadge from '@shared/components/ReadOnlyBadge';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { planApi } from '@features/billing/api/planApi';
import AdminPlanGrant from '@features/billing/components/AdminPlanGrant';
import type { Plan } from '@features/billing/types/plan';
import { Trash2, HardDrive, Cpu, Database, UserPlus, Package, X } from 'lucide-react';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  cardholderName?: string;
  isDefault: boolean;
  createdAt: string;
}

interface Subscription {
  id: string;
  organizationId: string;
  status: string;
  planName: string;
  planAmount: number;
  planCurrency: string;
  planInterval: string;
  planStorageGB: number;
  planAgentLimit: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  trialStart?: string;
  trialEnd?: string;
  createdAt: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  stripePriceId?: string;

  // Add-on fields (Plan Remodel)
  extraAgents: number;
  extraStorageGB: number;
  extraAgentCost: number;
  extraStorageCost: number;
  effectiveAgentLimit: number;
  effectiveStorageGB: number;
  totalMonthlyCost: number;

  // Computed effective status (backend checks period end dates)
  effectiveStatus?: string;
}

interface AddonsAvailable {
  extraAgent: boolean;
  extraStorage: boolean;
  extraAgentPrice?: number;
  extraAgentStorageGB?: number;
  extraStoragePrice: number;
  extraStorageAmountGB: number;
}

interface AddPaymentMethodFormProps {
  onSuccess: () => void;
}

export function AddPaymentMethodForm({ onSuccess }: AddPaymentMethodFormProps) {
  const { t } = useTranslation('billing');
  const passwordId = useId();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    if (!password) {
      setError(t('errors:errors.passwordRequired'));
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const mockPaymentMethodId = 'pm_card_visa_' + Date.now();
      const token = localStorage.getItem('auth_token');

      const response = await fetch('/api/v1/billing/payment-methods', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          paymentMethodId: mockPaymentMethodId,
          cardholderName: 'Test User',
          password_confirmation: password,
        }),
      });

      if (!response.ok)
        throw new Error(
          (await response.json()).error || t('billingMessages.paymentMethodAddFailed')
        );
      setPassword('');
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block">
          {t('paymentMethods.card_details', 'Detalhes do Cartão')}
        </label>
        <div className="p-4 border border-surface-border-light dark:border-surface-border-dark rounded-xl bg-surface-card-light dark:bg-surface-card-dark">
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
              },
            }}
          />
        </div>
      </div>

      <Input
        id={passwordId}
        label={t('labels.password_confirmation', 'Confirmação de Senha')}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t('placeholders.confirm_your_password')}
        required
      />

      {error && (
        <div className="p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark text-state-danger-light dark:text-state-danger-dark text-sm font-bold rounded-lg">
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        isLoading={isProcessing}
        className="w-full"
      >
        {t('paymentMethods.add')}
      </Button>
    </form>
  );
}

interface StorageUsage {
  storageUsedGB: number;
  storageLimitGB: number;
  usagePercentage: number;
  backupCount: number;
}
interface AgentUsage {
  agentCount: number;
  agentLimit: number;
  onlineAgentCount: number;
  usagePercentage: number;
}
interface BackupUsage {
  backupCount: number;
  backupLimit: number;
  completedBackups: number;
  usagePercentage: number;
}

export default function Billing() {
  const { t, i18n } = useTranslation('billing');
  const billingContactId = useId();
  const formatPrice = (cents: number, currency = 'BRL') => {
    const locale =
      i18n.language === 'pt-BR'
        ? 'pt-BR'
        : i18n.language === 'es'
          ? 'es-ES'
          : i18n.language === 'pt'
            ? 'pt-PT'
            : 'en-US';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(cents / 100);
    } catch {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    }
  };
  const formatBillingDate = (date: string) => {
    const locale =
      i18n.language === 'pt-BR'
        ? 'pt-BR'
        : i18n.language === 'es'
          ? 'es-ES'
          : i18n.language === 'pt'
            ? 'pt-PT'
            : 'en-US';
    return new Date(date).toLocaleDateString(locale);
  };
  const { shouldDisable: _disableBillingActions } = useImpersonationGuard();
  const { showConfirm } = useDialog();
  const [_paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [agentUsage, setAgentUsage] = useState<AgentUsage | null>(null);
  const [backupUsage, setBackupUsage] = useState<BackupUsage | null>(null);
  const [, setLoading] = useState(true);
  const [_showAddForm, _setShowAddForm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [upgradeNotice, setUpgradeNotice] = useState<{ text: string; id: number } | null>(null);
  const [upgradingTo, setUpgradingTo] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Billing Info State
  const [showBillingEdit, setShowBillingEdit] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [taxId, setTaxId] = useState('');
  const [billingPassword, setBillingPassword] = useState('');
  const [currentBillingEmail, setCurrentBillingEmail] = useState('');
  const [currentTaxId, setCurrentTaxId] = useState('');

  // Add-on purchase state
  const [addonsAvailable, setAddonsAvailable] = useState<AddonsAvailable | null>(null);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [addonType, setAddonType] = useState<'EXTRA_AGENT' | 'EXTRA_STORAGE'>('EXTRA_AGENT');
  const [addonQuantity, setAddonQuantity] = useState(1);
  const [purchasingAddon, setPurchasingAddon] = useState(false);
  const addonDialogRef = useRef<HTMLDivElement>(null);
  const addonOpenerRef = useRef<HTMLElement | null>(null);

  // Plans state - fetched from database
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [_plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    if (!upgradeNotice) return;
    const timeoutId = window.setTimeout(() => setUpgradeNotice(null), 8000);
    return () => window.clearTimeout(timeoutId);
  }, [upgradeNotice]);

  useEffect(() => {
    Promise.all([
      fetchPaymentMethods(),
      fetchSubscription(),
      fetchStorageUsage(),
      fetchAgentUsage(),
      fetchBackupUsage(),
      fetchBillingInformation(),
      fetchAvailablePlans(),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!showAddonModal) return;
    const previousOverflow = document.body.style.overflow;
    const opener = addonOpenerRef.current;
    const dialog = addonDialogRef.current;
    document.body.style.overflow = 'hidden';
    dialog?.querySelector<HTMLElement>('[data-dialog-initial-focus]')?.focus();

    const handleDialogKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowAddonModal(false);
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleDialogKeydown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeydown);
      document.body.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, [showAddonModal]);

  const openAddonModal = (type: 'EXTRA_AGENT' | 'EXTRA_STORAGE') => {
    addonOpenerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAddonType(type);
    setAddonQuantity(1);
    setShowAddonModal(true);
  };

  // Fetch functions omitted for brevity (same logic as before, just cleaner async/await)
  const fetchPaymentMethods = async () => {
    const res = await fetch('/api/v1/billing/payment-methods', {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (res.ok) setPaymentMethods((await res.json()).paymentMethods || []);
  };
  const fetchSubscription = async () => {
    try {
      const data = await planApi.getSubscription();
      setSubscription(data.subscription);
      setAddonsAvailable(data.addonsAvailable || null);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      // Fallback to old endpoint
      const res = await fetch('/api/v1/billing/subscription', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) setSubscription((await res.json()).subscription);
    }
  };
  const fetchStorageUsage = async () => {
    const res = await fetch('/api/v1/billing/storage-usage', {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (res.ok) setStorageUsage(await res.json());
  };
  const fetchAgentUsage = async () => {
    const res = await fetch('/api/v1/billing/agent-usage', {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (res.ok) setAgentUsage(await res.json());
  };
  const fetchBackupUsage = async () => {
    const res = await fetch('/api/v1/billing/backup-usage', {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (res.ok) setBackupUsage(await res.json());
  };
  const fetchBillingInformation = async () => {
    const res = await fetch('/api/v1/organizations', {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (res.ok) {
      const data = await res.json();
      if (data.organizations?.length) {
        setCurrentBillingEmail(data.organizations[0].billing_email || '');
        setCurrentTaxId(data.organizations[0].tax_id || '');
      }
    }
  };

  const fetchAvailablePlans = async () => {
    setPlansLoading(true);
    try {
      const plans = await planApi.getPublicPlans();
      // Sort by price ascending
      const sortedPlans = plans
        .filter((p) => p.isActive && p.isPublic && p.name !== 'Trial')
        .sort((a, b) => a.monthlyPrice - b.monthlyPrice);
      setAvailablePlans(sortedPlans);
    } catch (err) {
      console.error('Failed to fetch available plans:', err);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleUpdateBilling = async () => {
    if (!billingEmail || !billingPassword) return;
    try {
      const res = await fetch('/api/v1/billing/contact', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          billing_email: billingEmail,
          tax_id: taxId,
          password_confirmation: billingPassword,
        }),
      });
      if (res.ok) {
        setMessage({ type: 'success', text: t('billingMessages.billingContactUpdated') });
        setShowBillingEdit(false);
        setBillingPassword('');
        fetchBillingInformation();
      } else throw new Error((await res.json()).error);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleUpgrade = async (newPlanName: string) => {
    setMessage(null);
    setUpgradeNotice(null);
    setUpgradingTo(newPlanName);
    try {
      const res = await fetch('/api/v1/billing/subscription/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
          'Accept-Language': i18n.resolvedLanguage || i18n.language,
        },
        body: JSON.stringify({
          newPlanName,
          successUrl: `${window.location.origin}/billing?upgrade=success`,
          cancelUrl: `${window.location.origin}/billing`,
        }),
      });
      const responseBody = await res.text();
      let data: any = {};
      if (responseBody) {
        try {
          data = JSON.parse(responseBody);
        } catch {
          data = { error: responseBody };
        }
      }
      if (!res.ok) {
        throw new Error(
          typeof data.error === 'string' && data.error
            ? data.error
            : t('billingMessages.upgradeFailed')
        );
      }

      const confirmed = await showConfirm(
        t('pricing.confirmUpgrade'),
        t('pricing.upgradeConfirm', { plan: data.newPlanName }) +
          ' ' +
          t('pricing.proratedCharge') +
          ' ' +
          formatPrice(data.proratedAmount, subscription?.planCurrency || 'BRL'),
        'confirm'
      );
      if (confirmed) {
        if (data.sessionUrl) window.location.href = data.sessionUrl;
      }
    } catch (err: any) {
      setUpgradeNotice({
        text:
          err instanceof Error && err.message ? err.message : t('billingMessages.upgradeFailed'),
        id: Date.now(),
      });
    } finally {
      setUpgradingTo(null);
    }
  };

  const handleCancel = async () => {
    if (!subscription) return;
    const confirmed = await showConfirm(
      t('currentPlan.cancel'),
      t('currentPlan.cancelConfirm'),
      'warning'
    );
    if (!confirmed) return;

    setCancelling(true);
    try {
      const res = await fetch('/api/v1/billing/subscription/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          organizationId: subscription.organizationId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessage({
          type: 'success',
          text: data.message || t('notifications.subscriptionCancelled'),
        });
        // Drop the shared subscription cache so banners/badges reflect the
        // cancellation immediately instead of after the TTL.
        invalidateShared(SHARED_KEYS.subscription);
        fetchSubscription();
      } else {
        const contentType = res.headers.get('content-type');
        let errorMessage = t('errors.cancelFailed');
        if (contentType?.includes('application/json')) {
          const error = await res.json();
          errorMessage = error.error || errorMessage;
        } else {
          errorMessage = await res.text();
          // Try to parse JSON even if content-type is wrong
          try {
            const parsed = JSON.parse(errorMessage);
            errorMessage = parsed.error || errorMessage;
          } catch {
            // Use text as-is
          }
        }
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || t('errors.cancelFailed') });
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    if (!subscription) return;

    setReactivating(true);
    try {
      const res = await fetch('/api/v1/billing/subscription/reactivate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          organizationId: subscription.organizationId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessage({ type: 'success', text: data.message || t('notifications.reactivated') });
        // See handleCancel: keep the shared subscription cache honest.
        invalidateShared(SHARED_KEYS.subscription);
        fetchSubscription();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.error || t('errors.reactivateFailed') });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || t('errors.reactivateFailed') });
    } finally {
      setReactivating(false);
    }
  };

  // Helper to get plans that are upgrades from current plan
  const getUpgradeablePlans = (): Plan[] => {
    if (!subscription || availablePlans.length === 0) return [];
    const currentPlanIndex = availablePlans.findIndex((p) => p.name === subscription.planName);
    if (currentPlanIndex === -1) {
      // Current plan not in public plans, show all paid plans
      return availablePlans.filter((p) => p.monthlyPrice > 0);
    }
    // Return plans with higher price than current
    return availablePlans.filter((p) => p.monthlyPrice > subscription.planAmount);
  };

  const handleAddAddon = async () => {
    if (!subscription || addonQuantity < 1) return;

    setPurchasingAddon(true);
    try {
      const response = await planApi.addAddon({
        addonType,
        quantity: addonQuantity,
      });
      setMessage({
        type: 'success',
        text:
          addonType === 'EXTRA_AGENT'
            ? t('billingMessages.agentAdded', {
                count: addonQuantity,
                price: formatPrice(response.total_cost, subscription.planCurrency),
              })
            : t('billingMessages.storageAdded', {
                count: addonQuantity,
                price: formatPrice(response.total_cost, subscription.planCurrency),
              }),
      });
      setShowAddonModal(false);
      setAddonQuantity(1);
      fetchSubscription();
    } catch {
      setMessage({ type: 'error', text: t('billingMessages.addonPurchaseFailed') });
    } finally {
      setPurchasingAddon(false);
    }
  };

  const handleRemoveAddon = async (type: 'EXTRA_AGENT' | 'EXTRA_STORAGE') => {
    const confirmed = await showConfirm(
      type === 'EXTRA_AGENT' ? t('labels.remove_extra_agents') : t('labels.remove_extra_storage'),
      t(
        type === 'EXTRA_AGENT'
          ? 'billingMessages.removeAgentsConfirm'
          : 'billingMessages.removeStorageConfirm'
      ),
      'warning'
    );
    if (!confirmed) return;

    try {
      await planApi.removeAddon(type);
      setMessage({
        type: 'success',
        text: t(
          type === 'EXTRA_AGENT'
            ? 'billingMessages.agentsRemoved'
            : 'billingMessages.storageRemoved'
        ),
      });
      fetchSubscription();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || t('billingMessages.addonRemoveFailed') });
    }
  };

  return (
    <>
      {upgradeNotice && (
        <div
          key={upgradeNotice.id}
          role="alert"
          aria-live="assertive"
          className="fixed inset-x-4 top-4 z-[100] mx-auto flex w-full max-w-lg items-start gap-3 rounded-xl border border-state-danger-light/30 bg-state-danger-bg-light p-4 text-state-danger-light shadow-2xl dark:border-state-danger-dark/30 dark:bg-state-danger-bg-dark dark:text-state-danger-dark sm:left-auto sm:right-6 sm:mx-0"
        >
          <p className="min-w-0 flex-1 text-sm font-semibold">{upgradeNotice.text}</p>
          <button
            type="button"
            aria-label={t('billingDialog.close')}
            onClick={() => setUpgradeNotice(null)}
            className="-mr-2 -mt-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="space-y-8 animate-in fade-in duration-300">
        <ImpersonationSensitiveBanner />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                {t('title')}
              </h1>
              <ReadOnlyBadge />
            </div>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('subtitle')}
            </p>
          </div>
        </div>

        {message && (
          <div
            role={message.type === 'error' ? 'alert' : 'status'}
            aria-live={message.type === 'error' ? 'assertive' : 'polite'}
            className={`p-4 rounded-xl border font-bold text-sm ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {message.text}
          </div>
        )}

        <AdminPlanGrant
          onGranted={() => {
            fetchSubscription();
            fetchAgentUsage();
            fetchStorageUsage();
          }}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Subscription Status */}
          <Card className="lg:col-span-2 p-5 sm:p-8">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-6">
              {t('currentPlan.title')}
            </h2>

            {subscription ? (
              <div className="space-y-8">
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-accent-soft-light dark:bg-accent-soft-dark border border-accent-text-light/20 dark:border-accent-text-dark/20 rounded-2xl">
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-accent-text-light dark:text-accent-text-dark mb-1">
                      {t(
                        `subscriptionStatus.${(subscription.effectiveStatus || subscription.status).toLowerCase()}`,
                        {
                          defaultValue: subscription.effectiveStatus || subscription.status,
                        }
                      )}
                    </p>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                      {t(`pricing.planNames.${subscription.planName}`, {
                        defaultValue: subscription.planName,
                      })}
                    </h3>
                    <div className="text-text-muted-light dark:text-text-muted-dark font-medium">
                      {subscription.totalMonthlyCost &&
                      subscription.totalMonthlyCost > subscription.planAmount ? (
                        <div>
                          <span className="text-lg font-bold text-slate-900 dark:text-white">
                            {formatPrice(subscription.totalMonthlyCost, subscription.planCurrency)}
                          </span>
                          <span className="text-xs text-text-subtle-light dark:text-text-subtle-dark line-through ml-2">
                            {formatPrice(subscription.planAmount, subscription.planCurrency)}
                          </span>
                          <span className="text-xs text-text-subtle-light dark:text-text-subtle-dark ml-1">
                            /{' '}
                            {t(`billingInterval.${subscription.planInterval.toLowerCase()}`, {
                              defaultValue: subscription.planInterval,
                            })}
                          </span>
                          {(subscription.extraAgentCost || 0) > 0 && (
                            <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark mt-1">
                              {t('labels.includes', 'Inclui')}{' '}
                              {formatPrice(
                                subscription.extraAgentCost || 0,
                                subscription.planCurrency
                              )}{' '}
                              {t('labels.for_extra_agents', 'para')}{' '}
                              {t('pricing.agents', { count: subscription.extraAgents })}
                            </p>
                          )}
                          {(subscription.extraStorageCost || 0) > 0 && (
                            <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark">
                              {t('labels.includes', 'Inclui')}{' '}
                              {formatPrice(
                                subscription.extraStorageCost || 0,
                                subscription.planCurrency
                              )}{' '}
                              {t('labels.for_extra_storage', 'para armazenamento extra')}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span>
                          {formatPrice(subscription.planAmount, subscription.planCurrency)} /{' '}
                          {t(`billingInterval.${subscription.planInterval.toLowerCase()}`, {
                            defaultValue: subscription.planInterval,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant={
                      toneToBadgeVariant[
                        getStatusTone(subscription.effectiveStatus || subscription.status)
                      ]
                    }
                    className="text-sm px-3 py-1"
                  >
                    {t(
                      `subscriptionStatus.${(subscription.effectiveStatus || subscription.status).toLowerCase()}`,
                      {
                        defaultValue: subscription.effectiveStatus || subscription.status,
                      }
                    )}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <UsageMetric
                    icon={<HardDrive />}
                    label={t('usage.storage')}
                    used={storageUsage?.storageUsedGB ?? 0}
                    total={
                      storageUsage?.storageLimitGB ||
                      subscription.effectiveStorageGB ||
                      subscription.planStorageGB ||
                      0
                    }
                    unit="GB"
                    locale={i18n.language}
                    t={t}
                  />
                  <UsageMetric
                    icon={<Cpu />}
                    label={t('usage.agents')}
                    used={agentUsage?.agentCount ?? 0}
                    total={
                      agentUsage?.agentLimit ||
                      subscription.effectiveAgentLimit ||
                      subscription.planAgentLimit ||
                      0
                    }
                    unit=""
                    locale={i18n.language}
                    t={t}
                  />
                  <UsageMetric
                    icon={<Database />}
                    label={t('usage.backups')}
                    used={backupUsage?.backupCount ?? 0}
                    total={backupUsage?.backupLimit || 0}
                    unit=""
                    locale={i18n.language}
                    t={t}
                  />
                </div>

                {/* Add-on Purchase Section */}
                {addonsAvailable &&
                  (addonsAvailable.extraAgent || addonsAvailable.extraStorage) && (
                    <div className="p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-2xl">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="w-5 h-5 text-state-info-light dark:text-state-info-dark" />
                        <p className="font-bold text-state-info-light dark:text-state-info-dark ">
                          {t('labels.add_resources')}
                        </p>
                      </div>
                      <p className="text-sm text-state-info-light dark:text-state-info-dark mb-4">
                        {t('addons.purchaseDescription')}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {addonsAvailable.extraAgent && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAddonModal('EXTRA_AGENT')}
                            className="border-state-info-light dark:border-state-info-dark text-state-info-light dark:text-state-info-dark hover:bg-state-info-light/20 dark:hover:bg-state-info-dark/20 "
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {t('addons.addAgent')}
                            {addonsAvailable.extraAgentPrice && (
                              <span className="ml-1 text-xs">
                                (
                                {formatPrice(
                                  addonsAvailable.extraAgentPrice,
                                  subscription.planCurrency
                                )}
                                )
                              </span>
                            )}
                          </Button>
                        )}
                        {addonsAvailable.extraStorage && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAddonModal('EXTRA_STORAGE')}
                            className="border-state-info-light dark:border-state-info-dark text-state-info-light dark:text-state-info-dark hover:bg-state-info-light/20 dark:hover:bg-state-info-dark/20 "
                          >
                            <HardDrive className="w-4 h-4 mr-2" />
                            {t('addons.addStorage')}
                            <span className="ml-1 text-xs">
                              (
                              {formatPrice(
                                addonsAvailable.extraStoragePrice,
                                subscription.planCurrency
                              )}
                              {' / '}
                              {addonsAvailable.extraStorageAmountGB} GB)
                            </span>
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                {/* Dynamic Plan Upgrade Section */}
                {(() => {
                  const upgradeablePlans = getUpgradeablePlans();
                  if (upgradeablePlans.length === 0) return null;

                  return (
                    <div className="p-4 rounded-2xl bg-accent-soft-light dark:bg-accent-soft-dark border border-accent-text-light/20 dark:border-accent-text-dark/20">
                      <p className="font-bold mb-3 text-accent-text-light dark:text-accent-text-dark">
                        {t('upgrade.title')}
                      </p>
                      {subscription.planName === 'Trial' && (
                        <p className="text-sm text-accent-text-light dark:text-accent-text-dark mb-4">
                          {t('upgrade.trialEnds', {
                            date: subscription.trialEnd
                              ? formatBillingDate(subscription.trialEnd)
                              : t('billingMessages.soon'),
                          })}
                        </p>
                      )}
                      <div
                        className={`grid gap-3 ${upgradeablePlans.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}
                      >
                        {upgradeablePlans.slice(0, 2).map((plan) => (
                          <div
                            key={plan.id}
                            className="p-3 bg-surface-card-light dark:bg-surface-card-dark rounded-xl border border-accent-text-light/20 dark:border-accent-text-dark/20"
                          >
                            <p className="font-bold text-slate-900 dark:text-white">
                              {t(`pricing.planNames.${plan.name}`, {
                                defaultValue: plan.displayName || plan.name,
                              })}
                            </p>
                            <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                              {t('pricing.storageAmount', { amount: plan.storageIncludedGB })} •{' '}
                              {t('pricing.agents', { count: plan.agentsIncluded })}
                            </p>
                            <p className="text-lg font-black text-slate-900 dark:text-white">
                              {formatPrice(plan.monthlyPrice, plan.currency)}
                              <span className="text-xs font-medium text-text-subtle-light dark:text-text-subtle-dark">
                                {t('pricing.perMonth')}
                              </span>
                            </p>
                            <Button
                              onClick={() => handleUpgrade(plan.name)}
                              isLoading={upgradingTo === plan.name}
                              className="w-full mt-2"
                              size="sm"
                            >
                              {t('pricing.cta.upgrade')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Cancel Subscription */}
                {subscription.status === 'ACTIVE' && !subscription.cancelAtPeriodEnd && (
                  <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark border-l-4 border-l-state-danger-light dark:border-l-state-danger-dark rounded-2xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">
                          {t('currentPlan.cancel')}
                        </p>
                        <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                          {t('currentPlan.cancelConfirm')}
                        </p>
                      </div>
                      <Button
                        onClick={handleCancel}
                        isLoading={cancelling}
                        variant="outline"
                        className="border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark "
                      >
                        {t('currentPlan.cancel')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Reactivate Subscription */}
                {subscription.status === 'ACTIVE' && subscription.cancelAtPeriodEnd && (
                  <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark border-l-4 border-l-state-warn-light dark:border-l-state-warn-dark rounded-2xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">
                          {t('labels.subscription_ending')}
                        </p>
                        <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                          {t('currentPlan.expires', {
                            date: subscription.currentPeriodEnd
                              ? formatBillingDate(subscription.currentPeriodEnd)
                              : '-',
                          })}
                        </p>
                      </div>
                      <Button
                        onClick={handleReactivate}
                        isLoading={reactivating}
                        className="bg-state-warn-light hover:opacity-90 dark:bg-state-warn-dark"
                      >
                        {t('notifications.reactivated')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-text-muted-light dark:text-text-muted-dark mb-4">
                  {t('labels.no_active_subscription_found')}
                </p>
                <Button onClick={() => (window.location.href = '/pricing')}>
                  {t('labels.view_plans')}
                </Button>
              </div>
            )}
          </Card>

          {/* Total Monthly Cost */}
          <div className="space-y-6">
            {subscription && (
              <Card className="p-6 bg-accent-soft-light dark:bg-accent-soft-dark border-accent-text-light/20 dark:border-accent-text-dark/20">
                <h2 className="text-lg font-black text-slate-900 dark:text-white mb-4">
                  {t('labels.total_monthly_cost')}
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-text-muted-light dark:text-text-muted-dark">
                      {t('labels.base_plan', 'Plano base')} (
                      {t(`pricing.planNames.${subscription.planName}`, {
                        defaultValue: subscription.planName,
                      })}
                      )
                    </span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {formatPrice(subscription.planAmount, subscription.planCurrency)}
                    </span>
                  </div>
                  {(subscription.extraAgentCost || 0) > 0 && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-text-muted-light dark:text-text-muted-dark">
                        {t('labels.extra_agents', 'Agentes extras')} ({subscription.extraAgents})
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 dark:text-white">
                          {formatPrice(subscription.extraAgentCost || 0, subscription.planCurrency)}
                        </span>
                        <button
                          onClick={() => handleRemoveAddon('EXTRA_AGENT')}
                          className="p-1 hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark rounded text-state-danger-light dark:text-state-danger-dark"
                          title={t('labels.remove_extra_agents')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                  {(subscription.extraStorageCost || 0) > 0 && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-text-muted-light dark:text-text-muted-dark">
                        {t('labels.extra_storage', 'Armazenamento extra')} (
                        {subscription.extraStorageGB} GB)
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 dark:text-white">
                          {formatPrice(
                            subscription.extraStorageCost || 0,
                            subscription.planCurrency
                          )}
                        </span>
                        <button
                          onClick={() => handleRemoveAddon('EXTRA_STORAGE')}
                          className="p-1 hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark rounded text-state-danger-light dark:text-state-danger-dark"
                          title={t('labels.remove_extra_storage')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="border-t border-accent-text-light/20 dark:border-accent-text-dark/20 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-slate-900 dark:text-white">
                        {t('labels.total_monthly_cost')}
                      </span>
                      <span className="text-2xl font-black text-brand-600 dark:text-brand-400">
                        {formatPrice(
                          subscription.totalMonthlyCost || subscription.planAmount,
                          subscription.planCurrency
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                      {t('labels.billed_monthly')}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <Card className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-black text-slate-900 dark:text-white">
                  {t('billing_contact', 'Contato de Faturamento')}
                </h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowBillingEdit(!showBillingEdit);
                    setBillingEmail(currentBillingEmail);
                    setTaxId(currentTaxId);
                  }}
                >
                  {showBillingEdit ? t('common:actions.cancel') : t('common:actions.edit')}
                </Button>
              </div>

              {showBillingEdit ? (
                <div className="space-y-4 animate-in fade-in">
                  <Input
                    id={`${billingContactId}-email`}
                    label={t('labels.email', 'Email')}
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    type="email"
                    autoComplete="email"
                  />
                  <Input
                    id={`${billingContactId}-tax-id`}
                    label={t('labels.tax_id')}
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                  />
                  <Input
                    id={`${billingContactId}-password`}
                    label={t('labels.confirm_password', 'Confirmar Senha')}
                    type="password"
                    value={billingPassword}
                    onChange={(e) => setBillingPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <Button onClick={handleUpdateBilling} className="w-full">
                    {t('common:actions.save')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase">
                      {t('labels.email', 'Email')}
                    </p>
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      {currentBillingEmail || t('labels.not_set', 'Não definido')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase">
                      {t('labels.tax_id')}
                    </p>
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      {currentTaxId || t('labels.not_provided', 'Não fornecido')}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Add-on Purchase Modal */}
        {showAddonModal && addonsAvailable && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="billing-addon-title"
              ref={addonDialogRef}
              className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl p-5 sm:p-6 max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto shadow-2xl animate-in fade-in zoom-in duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  id="billing-addon-title"
                  className="text-lg font-black text-slate-900 dark:text-white"
                >
                  {addonType === 'EXTRA_AGENT'
                    ? t('labels.add_extra_agent', 'Adicionar Agente Extra')
                    : t('labels.add_extra_storage', 'Adicionar Armazenamento Extra')}
                </h3>
                <button
                  onClick={() => setShowAddonModal(false)}
                  type="button"
                  aria-label={t('billingDialog.close')}
                  data-dialog-initial-focus
                  className="min-h-11 min-w-11 flex items-center justify-center hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-lg"
                >
                  <X className="w-5 h-5 text-text-muted-light dark:text-text-muted-dark" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <span
                    id="billing-addon-quantity-label"
                    className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 block"
                  >
                    {t('labels.quantity', 'Quantidade')}
                  </span>
                  <div
                    className="flex items-center gap-3"
                    role="group"
                    aria-labelledby="billing-addon-quantity-label"
                  >
                    <button
                      type="button"
                      aria-label={t('billingDialog.decreaseQuantity')}
                      onClick={() => setAddonQuantity(Math.max(1, addonQuantity - 1))}
                      className="min-h-11 min-w-11 rounded-lg border border-surface-border-light dark:border-surface-border-dark flex items-center justify-center hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                    >
                      -
                    </button>
                    <output className="text-xl font-bold w-12 text-center" aria-live="polite">
                      {addonQuantity}
                    </output>
                    <button
                      type="button"
                      aria-label={t('billingDialog.increaseQuantity')}
                      onClick={() => setAddonQuantity(Math.min(10, addonQuantity + 1))}
                      className="min-h-11 min-w-11 rounded-lg border border-surface-border-light dark:border-surface-border-dark flex items-center justify-center hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl">
                  {addonType === 'EXTRA_AGENT' && addonsAvailable.extraAgentPrice && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('labels.cost_per_agent')}
                        </span>
                        <span className="font-bold">
                          {formatPrice(
                            addonsAvailable.extraAgentPrice,
                            subscription?.planCurrency || 'BRL'
                          )}
                        </span>
                      </div>
                      {addonsAvailable.extraAgentStorageGB && (
                        <div className="flex justify-between text-sm">
                          <span className="text-text-muted-light dark:text-text-muted-dark">
                            {t('labels.bonus_storage_per_agent')}
                          </span>
                          <span className="font-bold text-state-ok-light dark:text-state-ok-dark">
                            +{addonsAvailable.extraAgentStorageGB} GB
                          </span>
                        </div>
                      )}
                      <div className="border-t border-surface-border-light dark:border-surface-border-dark pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="font-bold text-slate-900 dark:text-white">
                            {t('labels.total_monthly_cost')}
                          </span>
                          <span className="font-black text-brand-600 dark:text-brand-400">
                            {formatPrice(
                              addonsAvailable.extraAgentPrice * addonQuantity,
                              subscription?.planCurrency || 'BRL'
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {addonType === 'EXTRA_STORAGE' && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('labels.cost_per_block')}
                        </span>
                        <span className="font-bold">
                          {formatPrice(
                            addonsAvailable.extraStoragePrice,
                            subscription?.planCurrency || 'BRL'
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted-light dark:text-text-muted-dark">
                          {t('labels.storage_per_block')}
                        </span>
                        <span className="font-bold">{addonsAvailable.extraStorageAmountGB} GB</span>
                      </div>
                      <div className="border-t border-surface-border-light dark:border-surface-border-dark pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="font-bold text-slate-900 dark:text-white">
                            {t('labels.total_monthly_cost')}
                          </span>
                          <span className="font-black text-brand-600 dark:text-brand-400">
                            {formatPrice(
                              addonsAvailable.extraStoragePrice * addonQuantity,
                              subscription?.planCurrency || 'BRL'
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-sm text-text-muted-light dark:text-text-muted-dark">
                            {t('labels.additional_storage')}
                          </span>
                          <span className="font-bold text-state-ok-light dark:text-state-ok-dark">
                            +{addonsAvailable.extraStorageAmountGB * addonQuantity} GB
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                  {t(
                    'addon_charge_note',
                    'Esta cobrança será adicionada à sua próxima fatura. Você pode modificar ou cancelar add-ons a qualquer momento.'
                  )}
                </p>

                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setShowAddonModal(false)}
                    className="flex-1"
                  >
                    {t('billingDialog.cancel')}
                  </Button>
                  <Button onClick={handleAddAddon} isLoading={purchasingAddon} className="flex-1">
                    {t('billingDialog.confirmPurchase')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

interface UsageMetricProps {
  icon: ReactNode;
  label: string;
  used: number;
  total: number;
  unit: '' | 'GB';
  locale: string;
  t: (key: string) => string;
}

function UsageMetric({ icon, label, used, total, unit, locale, t }: UsageMetricProps) {
  const hasLimit = Number.isFinite(Number(total)) && Number(total) > 0;
  const percent = hasLimit ? (Number(used) / Number(total)) * 100 : null;
  const numberLocale =
    locale === 'pt' ? 'pt-PT' : locale === 'pt-BR' ? 'pt-BR' : locale === 'es' ? 'es-ES' : 'en-US';
  const formatNumber = (value: number) =>
    new Intl.NumberFormat(numberLocale, {
      minimumFractionDigits: unit === 'GB' ? 2 : 0,
      maximumFractionDigits: unit === 'GB' ? 2 : 0,
    }).format(value);
  const displayUsed = formatNumber(Number(used) || 0);
  const displayTotal = hasLimit ? formatNumber(Number(total)) : null;
  const overLimit = percent !== null && percent > 90;

  return (
    <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark">
      <div className="flex justify-between items-start mb-2">
        <div className="p-2 bg-surface-card-light dark:bg-surface-card-dark rounded-lg shadow-sm text-text-muted-light dark:text-text-muted-dark">
          {icon}
        </div>
        <span
          className={`text-xs font-bold px-2 py-1 rounded-full ${overLimit ? 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark text-state-danger-light dark:text-state-danger-dark' : 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark'}`}
        >
          {percent === null ? '—' : `${Math.round(percent)}%`}
        </span>
      </div>
      <p className="text-xs font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-1">
        {label}
      </p>
      <p className="text-lg font-black text-slate-900 dark:text-white">
        {displayUsed}
        {unit ? ` ${unit}` : ''}{' '}
        {hasLimit ? (
          <span className="text-sm text-text-subtle-light dark:text-text-subtle-dark font-medium">
            / {displayTotal}
            {unit ? ` ${unit}` : ''}
          </span>
        ) : (
          <span className="block text-xs text-text-subtle-light dark:text-text-subtle-dark font-medium">
            {t('usage.limitUnavailable')}
          </span>
        )}
      </p>
      {percent !== null && (
        <div
          className="w-full bg-surface-border-light dark:bg-surface-border-dark h-1.5 rounded-full mt-3 overflow-hidden"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(Math.round(percent), 100)}
        >
          <div
            className={`h-full rounded-full ${overLimit ? 'bg-state-danger-light dark:bg-state-danger-dark' : 'bg-brand-600 dark:bg-brand-400'}`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
